/**
 * PII Redactor — fine-grained inline replacement of PII patterns in text.
 *
 * Phase 4 of the snapshot feature. Phase 1 redacts whole text nodes only;
 * this module replaces matches *inline*, preserving surrounding prose:
 *   "Contact us at foo@bar.com for help" → "Contact us at [REDACTED] for help"
 *
 * Two entry points:
 *   - `redactString(text)` — pure string-in/string-out, used in tests and
 *     callable independently if a non-DOM caller ever needs it.
 *   - `redactTextNodes(root)` — walks every text node under `root` and
 *     replaces PII inline, skipping subtrees marked with `data-no-redact`,
 *     and skipping `<code>` / `<pre>` (legitimate examples).
 *
 * Design notes:
 *   - Patterns are tuned to avoid common false positives. SSN requires
 *     dashes/spaces (so 9-digit order numbers survive); credit cards must
 *     pass a Luhn check (so 16-digit tracking IDs survive); IPs reject any
 *     octet > 255 (so version strings like 1.2.3.999 survive).
 *   - We bias toward under-redaction in ambiguous cases — better to leak
 *     a tracking number than corrupt a customer's real prose.
 */

const REDACTED = '[REDACTED]';

// ----- Regex catalog ---------------------------------------------------------
//
// Every pattern uses the `g` flag so .replace() can hit multiple matches in
// one string. Anchors (`\b` or surrounding-char checks) are tuned per pattern.

// Email: standard local@domain.tld, fairly permissive on the local part.
const EMAIL_RE =
  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;

// US/intl phone: optional `+1 ` prefix, optional parens around area code,
// 3-3-4 digit groups separated by space/dash/dot. Requires at least one
// separator so bare 10-digit numbers don't match (those are usually IDs).
const PHONE_RE =
  /(?:\+\d{1,3}[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]\d{3}[\s.-]\d{4}/g;

// SSN: XXX-XX-XXXX, dashes or spaces (NOT bare 9 digits — too many false
// positives on order numbers, IDs, etc.).
const SSN_RE = /\b\d{3}[\s-]\d{2}[\s-]\d{4}\b/g;

// IPv4: four 1-3 digit octets, dot-separated. We post-filter for "all
// octets <= 255" so version strings like 1.2.3.999 don't match.
const IPV4_RE = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g;

// URL: http(s):// or www. then non-whitespace. Greedy through path/query.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

// Credit card candidate: 13-19 digits in 4-4-4-4 (or contiguous) format,
// allowing dashes/spaces between groups. Luhn-checked before redacting.
// Must end on a digit (not a separator) — prevents greedy matching of
// trailing whitespace.
const CC_CANDIDATE_RE = /\b\d(?:[ -]?\d){12,18}\b/g;

// ----- Luhn check ------------------------------------------------------------

function luhnValid(digits: string): boolean {
  // digits already stripped of separators by caller
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48; // '0' = 48
    if (n < 0 || n > 9) return false;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

// ----- IPv4 octet bounds check ----------------------------------------------

function ipv4Valid(match: string): boolean {
  const parts = match.split('.');
  if (parts.length !== 4) return false;
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n) || n < 0 || n > 255) return false;
  }
  return true;
}

// ----- Public: string redactor ----------------------------------------------

/**
 * Replace every PII match in `text` with `[REDACTED]`. Surrounding prose
 * is preserved.
 *
 * Order matters: URLs first (so `http://...` doesn't get mangled by IPv4
 * matching), then emails, then more specific numeric patterns.
 */
export function redactString(text: string): string {
  if (!text) return text;
  let out = text;

  out = out.replace(URL_RE, REDACTED);
  out = out.replace(EMAIL_RE, REDACTED);
  out = out.replace(PHONE_RE, REDACTED);
  out = out.replace(SSN_RE, REDACTED);
  out = out.replace(IPV4_RE, (m) => (ipv4Valid(m) ? REDACTED : m));
  out = out.replace(CC_CANDIDATE_RE, (m) => {
    const digits = m.replace(/[\s-]/g, '');
    return luhnValid(digits) ? REDACTED : m;
  });

  return out;
}

// ----- Public: DOM walker ---------------------------------------------------

/**
 * Tag names whose text we never touch — code samples, preformatted blocks,
 * and `<style>` content. `<script>` is already removed earlier in the
 * snapshot pipeline, so it's not in this list.
 */
const SKIP_TAGS = new Set(['CODE', 'PRE', 'STYLE', 'TEXTAREA']);

/**
 * Walks every text node under `root` and redacts PII inline. Skips:
 *   - text nodes inside any ancestor with `[data-no-redact]`
 *   - text nodes inside `<code>`, `<pre>`, `<style>`, `<textarea>`
 *
 * Mutates the DOM in place.
 */
export function redactTextNodes(root: Element): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  // Collect first, mutate after — walker behaviour with mutation is
  // implementation-defined.
  const toUpdate: { node: Text; value: string }[] = [];

  let node = walker.nextNode() as Text | null;
  while (node) {
    if (!isInSkippedSubtree(node)) {
      const original = node.nodeValue || '';
      const replaced = redactString(original);
      if (replaced !== original) {
        toUpdate.push({ node, value: replaced });
      }
    }
    node = walker.nextNode() as Text | null;
  }

  for (const { node: textNode, value } of toUpdate) {
    textNode.nodeValue = value;
  }
}

function isInSkippedSubtree(node: Node): boolean {
  let cur: Node | null = node.parentNode;
  while (cur && cur.nodeType === Node.ELEMENT_NODE) {
    const el = cur as Element;
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.hasAttribute('data-no-redact')) return true;
    cur = el.parentNode;
  }
  return false;
}
